import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useApi,
  storageApiRef,
  errorApiRef,
} from '@backstage/core-plugin-api';
import { JsonValue } from '@backstage/types';
import {
  SystemTile,
  UserTilePreferences,
  DEFAULT_TILES,
  DEFAULT_TILE_IDS,
  STORAGE_BUCKET,
  PREFERENCES_KEY,
} from './tiles';

const TILE_MAP = new Map(DEFAULT_TILES.map(t => [t.id, t]));

function isUserTilePreferences(v: unknown): v is UserTilePreferences {
  if (typeof v !== 'object' || v === null) return false;
  const obj = v as Record<string, unknown>;
  return (
    Array.isArray(obj.visibleTileIds) &&
    Array.isArray(obj.dismissedNewTileIds) &&
    typeof obj.version === 'number'
  );
}

function resolvePreferences(prefs: UserTilePreferences | undefined): {
  visibleTiles: SystemTile[];
  hiddenTiles: SystemTile[];
  newTileIds: string[];
  isCustomized: boolean;
} {
  if (!prefs) {
    return {
      visibleTiles: DEFAULT_TILES,
      hiddenTiles: [],
      newTileIds: [],
      isCustomized: false,
    };
  }

  // Filter out stale IDs not in current registry
  const validVisibleIds = prefs.visibleTileIds.filter(id => TILE_MAP.has(id));
  const validDismissedIds = prefs.dismissedNewTileIds.filter(id =>
    TILE_MAP.has(id),
  );

  // Detect new tiles: in registry but not in visible or dismissed lists
  const knownIds = new Set([...validVisibleIds, ...validDismissedIds]);
  const newTileIds = DEFAULT_TILE_IDS.filter(id => !knownIds.has(id));

  // New tiles auto-show at the end of visible list
  const allVisibleIds = [...validVisibleIds, ...newTileIds];

  const visibleTiles = allVisibleIds
    .map(id => TILE_MAP.get(id))
    .filter((t): t is SystemTile => t !== undefined);

  const visibleIdSet = new Set(allVisibleIds);
  const hiddenTiles = DEFAULT_TILES.filter(t => !visibleIdSet.has(t.id));

  return {
    visibleTiles,
    hiddenTiles,
    newTileIds,
    isCustomized: true,
  };
}

export function useHomepageTiles() {
  const storageApi = useApi(storageApiRef);
  const errorApi = useApi(errorApiRef);
  const bucket = useMemo(
    () => storageApi.forBucket(STORAGE_BUCKET),
    [storageApi],
  );

  // Read initial snapshot — if data is available synchronously, skip loading state
  const [rawValue, setRawValue] = useState<JsonValue | undefined>(() => {
    try {
      const snap = bucket.snapshot<JsonValue>(PREFERENCES_KEY);
      return snap.presence === 'present' ? snap.value : undefined;
    } catch {
      return undefined;
    }
  });

  const [loading, setLoading] = useState<boolean>(() => {
    try {
      const snap = bucket.snapshot<JsonValue>(PREFERENCES_KEY);
      return snap.presence !== 'present' && snap.presence !== 'absent';
    } catch {
      return false;
    }
  });

  // Subscribe to changes for reactivity
  useEffect(() => {
    const subscription = bucket
      .observe$<JsonValue>(PREFERENCES_KEY)
      .subscribe({
        next: snapshot => {
          setRawValue(snapshot.value ?? undefined);
          setLoading(false);
        },
      });
    return () => subscription.unsubscribe();
  }, [bucket]);

  const preferences = isUserTilePreferences(rawValue) ? rawValue : undefined;

  const resolved = useMemo(
    () => resolvePreferences(preferences),
    [preferences],
  );

  const savePreferences = useCallback(
    async (prefs: UserTilePreferences): Promise<boolean> => {
      try {
        await bucket.set(PREFERENCES_KEY, prefs as unknown as JsonValue);
        return true;
      } catch (err) {
        errorApi.post(
          new Error(
            `Failed to save homepage tile preferences: ${err instanceof Error ? err.message : String(err)}`,
          ),
        );
        return false;
      }
    },
    [bucket, errorApi],
  );

  const resetPreferences = useCallback(async (): Promise<boolean> => {
    try {
      await bucket.remove(PREFERENCES_KEY);
      return true;
    } catch (err) {
      errorApi.post(
        new Error(
          `Failed to reset homepage tile preferences: ${err instanceof Error ? err.message : String(err)}`,
        ),
      );
      return false;
    }
  }, [bucket, errorApi]);

  return {
    ...resolved,
    loading,
    preferences,
    savePreferences,
    resetPreferences,
  };
}
